import { create } from 'zustand';
import { apiClient } from '../lib/api';

/**
 * Admin-side RBAC editing store.
 *
 * Separate from `rbacStore` (which holds the *current* user's own effective
 * permissions and drives the sidebar). This one loads the full role/feature
 * catalog so an admin can map features to any role and save the result.
 *
 * All calls go through the People service, reachable as `/people/rbac/*`
 * (`/api` is already in the axios baseURL).
 */

export interface CatalogPermission {
  id: string;
  action: string;
}
export interface CatalogResource {
  id: string;
  name: string;
  moduleName: string;
  permissions: CatalogPermission[];
}
export interface CatalogModule {
  id: string;
  name: string;
  displayOrder: number | null;
  resources: CatalogResource[];
}

export interface Role {
  id: string;
  name: string;
  description?: string | null;
  code?: string | null;
  is_system?: boolean;
}

/** A granted permission for the selected role: permissionId -> scope. */
export type GrantMap = Record<string, string>;

export interface HubDef {
  key: string;
  label: string;
}

type Scope = 'Self' | 'Team' | 'Company';

interface AdminRbacState {
  roles: Role[];
  catalog: CatalogModule[];
  rolesLoading: boolean;
  catalogLoading: boolean;
  error: string | null;

  /** Hub (module) access catalog + current role's grants. */
  hubCatalog: HubDef[];
  roleHubs: string[];
  hubsDirty: boolean;

  /** Grants for the role currently being edited. */
  selectedRoleId: string | null;
  grants: GrantMap;
  grantsLoading: boolean;
  dirty: boolean;
  saving: boolean;

  loadRoles: () => Promise<void>;
  loadCatalog: () => Promise<void>;
  loadHubCatalog: () => Promise<void>;
  selectRole: (roleId: string) => Promise<void>;

  /** Toggle a top-level hub (module) on/off for the selected role. */
  toggleHub: (hubKey: string, on: boolean) => void;
  saveHubs: () => Promise<void>;

  /** Toggle a single permission (action) on/off for the selected role. */
  togglePermission: (permissionId: string, on: boolean, scope?: Scope) => void;
  /** Turn a whole feature (resource) on/off — its View, or every action. */
  toggleResource: (resource: CatalogResource, on: boolean) => void;
  /** Change the scope applied to a feature's grants. */
  setResourceScope: (resource: CatalogResource, scope: Scope) => void;

  save: () => Promise<{ granted: number; affectedUsers: number }>;
  reset: () => void;
}

const DEFAULT_NEW_SCOPE: Scope = 'Company';

/**
 * The matrix models one scope per feature-action. When the stored data grants
 * the same permission at several scopes, collapse to the widest so a save can
 * never silently narrow access (Company/Department > Team > Self).
 */
const SCOPE_RANK: Record<string, number> = { Company: 3, Department: 3, Team: 2, Self: 1 };
function widerScope(a: string | undefined, b: string): string {
  if (!a) return b;
  return (SCOPE_RANK[b] ?? 0) > (SCOPE_RANK[a] ?? 0) ? b : a;
}

export const useAdminRbacStore = create<AdminRbacState>((set, get) => ({
  roles: [],
  catalog: [],
  rolesLoading: false,
  catalogLoading: false,
  error: null,

  hubCatalog: [],
  roleHubs: [],
  hubsDirty: false,

  selectedRoleId: null,
  grants: {},
  grantsLoading: false,
  dirty: false,
  saving: false,

  loadRoles: async () => {
    set({ rolesLoading: true, error: null });
    try {
      const res = await apiClient.get('/people/rbac/roles');
      const data = res.data?.data ?? res.data ?? [];
      set({ roles: data, rolesLoading: false });
    } catch (e: any) {
      set({ rolesLoading: false, error: e?.response?.data?.message || 'Failed to load roles' });
    }
  },

  loadCatalog: async () => {
    set({ catalogLoading: true, error: null });
    try {
      const res = await apiClient.get('/people/rbac/catalog');
      const data = res.data?.data ?? res.data ?? [];
      set({ catalog: data, catalogLoading: false });
    } catch (e: any) {
      set({ catalogLoading: false, error: e?.response?.data?.message || 'Failed to load catalog' });
    }
  },

  loadHubCatalog: async () => {
    try {
      const res = await apiClient.get('/people/rbac/hubs');
      set({ hubCatalog: res.data?.data ?? res.data ?? [] });
    } catch (e: any) {
      set({ error: e?.response?.data?.message || 'Failed to load hub catalog' });
    }
  },

  selectRole: async (roleId) => {
    set({ selectedRoleId: roleId, grantsLoading: true, grants: {}, roleHubs: [], hubsDirty: false, dirty: false, error: null });
    try {
      const [permRes, hubRes] = await Promise.all([
        apiClient.get(`/people/rbac/roles/${roleId}/permissions`),
        apiClient.get('/people/rbac/role-hubs'),
      ]);
      const rows: Array<{ permission_id: string; scope: string }> = permRes.data?.data ?? permRes.data ?? [];
      const map: GrantMap = {};
      for (const r of rows) map[r.permission_id] = widerScope(map[r.permission_id], r.scope);

      const hubRows: Array<{ role_id: string; hub_key: string }> = hubRes.data?.data ?? hubRes.data ?? [];
      const roleHubs = hubRows.filter((h) => h.role_id === roleId).map((h) => h.hub_key);

      set({ grants: map, roleHubs, grantsLoading: false });
    } catch (e: any) {
      set({ grantsLoading: false, error: e?.response?.data?.message || 'Failed to load role permissions' });
    }
  },

  toggleHub: (hubKey, on) => {
    const set1 = new Set(get().roleHubs);
    if (on) set1.add(hubKey); else set1.delete(hubKey);
    set({ roleHubs: Array.from(set1), hubsDirty: true });
  },

  saveHubs: async () => {
    const { selectedRoleId, roleHubs } = get();
    if (!selectedRoleId) throw new Error('No role selected');
    await apiClient.put(`/people/rbac/roles/${selectedRoleId}/hubs`, { hubs: roleHubs });
    set({ hubsDirty: false });
  },

  togglePermission: (permissionId, on, scope) => {
    const grants = { ...get().grants };
    if (on) {
      // Keep whatever scope the feature already uses; default new grants wide
      // only when nothing else on the feature pins a narrower scope.
      grants[permissionId] = scope || grants[permissionId] || DEFAULT_NEW_SCOPE;
    } else {
      delete grants[permissionId];
    }
    set({ grants, dirty: true });
  },

  toggleResource: (resource, on) => {
    const grants = { ...get().grants };
    const existingScope = resource.permissions
      .map((p) => grants[p.id])
      .find(Boolean) as Scope | undefined;
    if (on) {
      // Master-on grants View only (the "make it visible" action); the admin
      // then adds Create/Edit/Delete explicitly. If the resource has no View
      // action, grant every action so it is at least reachable.
      const view = resource.permissions.find((p) => p.action === 'View');
      const scope = existingScope || DEFAULT_NEW_SCOPE;
      if (view) {
        grants[view.id] = grants[view.id] || scope;
      } else {
        for (const p of resource.permissions) grants[p.id] = grants[p.id] || scope;
      }
    } else {
      for (const p of resource.permissions) delete grants[p.id];
    }
    set({ grants, dirty: true });
  },

  setResourceScope: (resource, scope) => {
    const grants = { ...get().grants };
    for (const p of resource.permissions) {
      if (grants[p.id]) grants[p.id] = scope;
    }
    set({ grants, dirty: true });
  },

  save: async () => {
    const { selectedRoleId, grants } = get();
    if (!selectedRoleId) throw new Error('No role selected');
    set({ saving: true, error: null });
    try {
      const body = {
        grants: Object.entries(grants).map(([permissionId, scope]) => ({ permissionId, scope })),
      };
      const res = await apiClient.put(`/people/rbac/roles/${selectedRoleId}/permissions`, body);
      const data = res.data?.data ?? res.data ?? {};
      set({ saving: false, dirty: false });
      return { granted: data.granted ?? 0, affectedUsers: data.affectedUsers ?? 0 };
    } catch (e: any) {
      set({ saving: false, error: e?.response?.data?.message || 'Failed to save permissions' });
      throw e;
    }
  },

  reset: () => set({ selectedRoleId: null, grants: {}, dirty: false, roleHubs: [], hubsDirty: false, error: null }),
}));
